import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Mail, Shield, Calendar, Edit, AlertTriangle, XCircle, Sparkles, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Layout } from '@/components/wiki/Layout';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, getAuthHeader, isPremium, isAdmin, isSuperAdmin } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchSubscriptions = async () => {
      try {
        const response = await axios.get(`${API}/subscriptions/my`, {
          headers: getAuthHeader()
        });
        setSubscriptions(response.data);
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSubscriptions();
  }, [user, authLoading, navigate, getAuthHeader]);

  const handleCancelSubscription = async (subscriptionId) => {
    setCancelling(subscriptionId);
    try {
      await axios.post(`${API}/subscriptions/${subscriptionId}/cancel`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Subscription cancelled. Your premium features remain active until the end of your billing period.');
      setSubscriptions(subs => subs.map(s => 
        s.id === subscriptionId ? { ...s, status: 'cancelled' } : s
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(null);
    }
  };

  if (authLoading || (!user && !authLoading)) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-10 bg-[#eaecf0] rounded w-1/4" />
            <div className="h-64 bg-[#eaecf0] rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!user) return null;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="profile-page">
        <h1 className="font-serif text-3xl font-bold text-[#202122] mb-8">My Profile</h1>

        <div className="grid gap-6">
          {/* User Info */}
          <div className="bg-white rounded-lg border border-[#a2a9b1] p-6">
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 rounded-full bg-[#36c] flex items-center justify-center text-white text-3xl font-bold">
                {user.username[0].toUpperCase()}
              </div>
              <div className="flex-1">
                <h2 className="font-serif text-2xl font-bold text-[#202122] mb-2">{user.username}</h2>
                <div className="space-y-2 text-[#54595d]">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <span>{user.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    <span className="capitalize">{user.role}</span>
                    {isPremium() && (
                      <span className="px-2 py-0.5 bg-[#eaf3ff] text-[#36c] text-xs rounded-full font-medium">
                        Premium
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>Joined {new Date(user.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Claim Credits */}
          <div className="bg-white rounded-lg border border-[#a2a9b1] p-6" data-testid="claim-credits-section">
            <h3 className="font-semibold text-[#202122] mb-4 flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-[#36c]" />
              Claim Credits
            </h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-[#36c]" data-testid="claim-credits-count">
                  {user.claim_credits || 0}
                </p>
                <p className="text-sm text-[#54595d]">Available credits to claim pages</p>
              </div>
              <Link to="/pricing">
                <Button className="bg-[#36c] hover:bg-[#2a4b8d] text-white" data-testid="buy-credits-btn">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Buy Credits
                </Button>
              </Link>
            </div>
            {(user.claim_credits || 0) > 0 && (
              <p className="mt-4 text-sm text-[#54595d] border-t border-[#c8ccd1] pt-4">
                Use your credits to claim any page and become its sponsor. Browse pages and click "Claim this Page" to use a credit.
              </p>
            )}
          </div>

          {/* Role Badges */}
          <div className="bg-white rounded-lg border border-[#a2a9b1] p-6">
            <h3 className="font-semibold text-[#202122] mb-4">Account Permissions</h3>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-[#eaecf0] text-[#54595d] rounded-full text-sm">
                Create Pages
              </span>
              <span className="px-3 py-1 bg-[#eaecf0] text-[#54595d] rounded-full text-sm">
                Edit Pages
              </span>
              <span className="px-3 py-1 bg-[#eaecf0] text-[#54595d] rounded-full text-sm">
                Start Discussions
              </span>
              {isPremium() && (
                <>
                  <span className="px-3 py-1 bg-[#eaf3ff] text-[#36c] rounded-full text-sm">
                    Add Social Links
                  </span>
                  <span className="px-3 py-1 bg-[#eaf3ff] text-[#36c] rounded-full text-sm">
                    Add Contact Info
                  </span>
                  <span className="px-3 py-1 bg-[#eaf3ff] text-[#36c] rounded-full text-sm">
                    Lock Pages
                  </span>
                </>
              )}
              {isAdmin() && (
                <>
                  <span className="px-3 py-1 bg-[#fef6e7] text-[#ac6600] rounded-full text-sm">
                    Admin Dashboard
                  </span>
                  <span className="px-3 py-1 bg-[#fef6e7] text-[#ac6600] rounded-full text-sm">
                    Manage Users
                  </span>
                </>
              )}
              {isSuperAdmin() && (
                <>
                  <span className="px-3 py-1 bg-[#f3e8ff] text-[#7c3aed] rounded-full text-sm">
                    Configure Pricing
                  </span>
                  <span className="px-3 py-1 bg-[#f3e8ff] text-[#7c3aed] rounded-full text-sm">
                    Review Reports
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Subscriptions */}
          <div className="bg-white rounded-lg border border-[#a2a9b1] p-6">
            <h3 className="font-semibold text-[#202122] mb-4">My Claimed Pages</h3>
            
            {subscriptions.some(s => s.status === 'active' && s.plan_type !== 'writer') && (
              <div className="mb-4 p-4 bg-[#fef6e7] border border-[#fc3] rounded-lg flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-[#ac6600] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[#ac6600] font-medium text-sm">Auto-Renewal Notice</p>
                  <p className="text-[#54595d] text-xs mt-1">
                    Your subscription will automatically renew at the end of each billing period until you cancel.
                  </p>
                </div>
              </div>
            )}
            
            {loading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-16 bg-[#eaecf0] rounded" />
                <div className="h-16 bg-[#eaecf0] rounded" />
              </div>
            ) : subscriptions.length > 0 ? (
              <div className="space-y-3">
                {subscriptions.map((sub) => (
                  <div key={sub.id} className="flex items-center justify-between p-4 bg-[#f8f9fa] rounded-lg border border-[#c8ccd1]">
                    <div className="flex-1">
                      {sub.page_slug ? (
                        <Link to={`/wiki/${sub.page_slug}`} className="font-medium text-[#36c] hover:underline transition-colors">
                          {sub.page_title || 'Untitled Page'}
                        </Link>
                      ) : (
                        <p className="font-medium text-[#202122]">{sub.page_title || 'Unknown Page'}</p>
                      )}
                      <p className="text-sm text-[#54595d]">
                        {sub.plan_type === 'monthly' ? 'Monthly' : sub.plan_type === 'yearly' ? 'Yearly' : 'Hire a Writer'} Plan • ${sub.amount}
                      </p>
                      <p className="text-xs text-[#72777d]">
                        {sub.status === 'cancelled' 
                          ? `Access until: ${new Date(sub.expires_at).toLocaleDateString()}`
                          : sub.plan_type === 'writer'
                            ? 'Lifetime access'
                            : `Renews: ${new Date(sub.expires_at).toLocaleDateString()}`
                        }
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        sub.status === 'active' ? 'bg-[#d5fdd5] text-[#14866d]' : 
                        sub.status === 'cancelled' ? 'bg-[#fef6e7] text-[#ac6600]' :
                        'bg-[#eaecf0] text-[#54595d]'
                      }`}>
                        {sub.status === 'cancelled' ? 'Cancelling' : sub.status}
                      </span>
                      
                      {sub.status === 'active' && sub.plan_type !== 'writer' && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-[#d33] hover:text-[#b32424] hover:bg-[#fee7e6]"
                              disabled={cancelling === sub.id}
                              data-testid={`cancel-sub-${sub.id}`}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Cancel
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-white border-[#a2a9b1]">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-[#202122]">Cancel Subscription?</AlertDialogTitle>
                              <AlertDialogDescription className="text-[#54595d]">
                                Are you sure you want to cancel this subscription? Your premium features will remain active until <strong className="text-[#202122]">{new Date(sub.expires_at).toLocaleDateString()}</strong>, after which your page will no longer be sponsored.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-[#f8f9fa] text-[#202122] border-[#a2a9b1] hover:bg-[#eaecf0]">
                                Keep Subscription
                              </AlertDialogCancel>
                              <AlertDialogAction 
                                className="bg-[#d33] text-white hover:bg-[#b32424]"
                                onClick={() => handleCancelSubscription(sub.id)}
                              >
                                Yes, Cancel
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-[#54595d] mb-4">You haven't claimed any pages yet.</p>
                <Link to="/">
                  <Button variant="outline" className="border-[#a2a9b1] text-[#36c] hover:bg-[#eaecf0]">Browse Pages</Button>
                </Link>
              </div>
            )}
          </div>

          {/* Admin Links */}
          {isAdmin() && (
            <div className="bg-white rounded-lg border border-[#a2a9b1] p-6">
              <h3 className="font-semibold text-[#202122] mb-4">Admin Tools</h3>
              <div className="flex gap-3">
                <Link to="/admin">
                  <Button variant="outline" className="border-[#a2a9b1] text-[#36c] hover:bg-[#eaecf0]" data-testid="admin-dashboard-link">
                    <Shield className="h-4 w-4 mr-2" />
                    Admin Dashboard
                  </Button>
                </Link>
                {isSuperAdmin() && (
                  <Link to="/admin/config">
                    <Button variant="outline" className="border-[#a2a9b1] text-[#36c] hover:bg-[#eaecf0]" data-testid="config-link">
                      <Edit className="h-4 w-4 mr-2" />
                      Configuration
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
